/**
 * reCAPTCHA v3 Service
 *
 * This service handles reCAPTCHA v3 token generation.
 * Make sure to set VUE_APP_RECAPTCHA_SITE_KEY in your environment variables.
 *
 * Hardening note: every async step below is bounded by a timeout. reCAPTCHA can
 * silently fail to resolve in real browsers (privacy extensions that stub out
 * window.grecaptcha, CSP/connect-src blocking its network calls, tracking
 * protection, etc.). Without a timeout an awaiting caller hangs forever. We
 * always fail open (return null) so the caller can proceed and the backend
 * decides whether a missing token is acceptable.
 *
 * IMPORTANT for callers: do NOT call grecaptcha.execute() (i.e. this function)
 * in direct response to a user interaction on a page that also performs heavy
 * re-renders (e.g. a Vuetify multi-step wizard). With browser tracking
 * prevention blocking reCAPTCHA's storage (notably Edge), execute() can
 * deadlock the main thread. Acquire the token against a stable DOM instead
 * (on mount / a refresh timer) and reuse the cached value. See IBRegister.vue.
 */

// Max time to wait for the reCAPTCHA script to become usable.
const LOAD_TIMEOUT_MS = 8000;
// Max time to wait for grecaptcha.execute() to produce a token.
const EXECUTE_TIMEOUT_MS = 8000;

/**
 * Loads reCAPTCHA script and initializes it
 * @param {string} siteKey - The reCAPTCHA site key
 * @returns {Promise} - Promise that resolves when reCAPTCHA is loaded
 */
function loadRecaptcha(siteKey) {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        resolve();
      });
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      // Wait for it to load — bounded so a never-appearing grecaptcha (e.g.
      // blocked by an extension/CSP) can't leave this interval running forever.
      const startedAt = Date.now();
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          clearInterval(checkInterval);
          window.grecaptcha.ready(() => {
            resolve();
          });
        } else if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
          clearInterval(checkInterval);
          reject(new Error('Timed out waiting for reCAPTCHA to initialize'));
        }
      }, 100);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    // Guard against onload firing but grecaptcha.ready() never calling back.
    const loadTimer = setTimeout(() => {
      reject(new Error('Timed out loading reCAPTCHA script'));
    }, LOAD_TIMEOUT_MS);

    script.onload = () => {
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        clearTimeout(loadTimer);
        reject(new Error('reCAPTCHA loaded but is unavailable'));
        return;
      }
      window.grecaptcha.ready(() => {
        clearTimeout(loadTimer);
        resolve();
      });
    };
    script.onerror = () => {
      clearTimeout(loadTimer);
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Gets a reCAPTCHA v3 token
 * @param {string} action - The action name (e.g., 'register', 'register_collapsed', 'register_user')
 * @returns {Promise<string>} - Promise that resolves with the reCAPTCHA token
 */
export async function getRecaptchaToken(action = 'register') {
  const siteKey = process.env.VUE_APP_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    console.warn('VUE_APP_RECAPTCHA_SITE_KEY is not set. reCAPTCHA will not work.');
    return null;
  }

  try {
    await loadRecaptcha(siteKey);

    let timeoutId;
    const executeToken = new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action })
          .then((token) => {
            resolve(token);
          })
          .catch((error) => {
            console.error('reCAPTCHA execution error:', error);
            reject(error);
          });
      });
    });

    // Bound execute() so a promise that never settles (stubbed grecaptcha,
    // blocked network call) can't hang the caller. Fail open with null.
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('reCAPTCHA execution timed out')),
        EXECUTE_TIMEOUT_MS
      );
    });

    try {
      return await Promise.race([executeToken, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('reCAPTCHA error, continuing without token:', error);
    return null;
  }
}

export default {
  getRecaptchaToken
};
